import { Fragment, type ReactNode } from 'react';
import type { FarmState, Monta } from '../domain/model';
import { createDomain } from '../domain/farm';

export interface EntityReference { type: string; id: string }
export interface ResolvedEntityReference { tab: 'cerdas' | 'verracos' | 'lotes'; id: string; label: string }

/** Only stored identifiers can navigate; orphaned records remain readable. */
export function resolveEntityReference(state: FarmState, reference?: EntityReference | null): ResolvedEntityReference | null {
  if (!reference?.id) return null;
  if (reference.type === 'tarea') {
    const task=state.tareasManual.find(record=>record.id===reference.id);
    return task?.destinatarioTipo&&task.destinatarioId&&['cerda','verraco','lote','camada'].includes(task.destinatarioTipo)
      ?resolveEntityReference(state,{type:task.destinatarioTipo,id:task.destinatarioId}):null;
  }
  if (reference.type === 'cerda') {
    const sow = state.cerdas.find(record => record.arete === reference.id);
    return sow ? { tab: 'cerdas', id: sow.arete, label: `Cerda ${sow.arete}` } : null;
  }
  if (reference.type === 'verraco') {
    const boar = state.verracos.find(record => record.id === reference.id);
    return boar ? { tab: 'verracos', id: boar.id, label: boar.nombre } : null;
  }
  if (reference.type === 'lote') {
    const lot = state.lotes.find(record => record.id === reference.id);
    return lot ? { tab: 'lotes', id: lot.id, label: `Lote ${lot.id}` } : null;
  }
  if (reference.type === 'camada') {
    const litter = state.camadas.find(record => record.id === reference.id);
    const birth = litter && state.partos.find(record => record.id === litter.partoId);
    const mother = litter && state.cerdas.find(record => record.arete === (birth?.arete || litter.arete));
    return mother ? { tab: 'cerdas', id: mother.arete, label: `Cerda ${mother.arete} (camada ${litter!.id})` } : null;
  }
  return null;
}

export function EntityLink({ state, navigate, reference, children, className = 'linklike' }: {
  state: FarmState;
  navigate: (tab: string, id?: string) => void;
  reference?: EntityReference | null;
  children: ReactNode;
  className?: string;
}) {
  const target = resolveEntityReference(state, reference);
  if (!target) return <>{children}</>;
  return <button type="button" className={className} aria-label={`Ver ficha de ${target.label}`} onClick={event => {
    event.stopPropagation();
    navigate(target.tab, target.id);
  }}>{children}</button>;
}

/** External fathers and deleted boars keep their original readable labels. */
export function MatingBoarLinks({ state, navigate, mating }: {
  state: FarmState;
  navigate: (tab: string, id?: string) => void;
  mating?: Monta | null;
}) {
  if (!mating) return <>No registrado</>;
  const domain = createDomain(state);
  const ids = domain.verracoIdsDeMonta(mating);
  if (!ids.length) return <>{domain.nombresVerracosDeMonta(mating)}</>;
  return <>{ids.map((id, index) => <Fragment key={id}>{index > 0 && ' + '}<EntityLink state={state} navigate={navigate} reference={{ type: 'verraco', id }}>{state.verracos.find(record => record.id === id)?.nombre || 'Verraco eliminado'}</EntityLink></Fragment>)}</>;
}
